import { useState, useEffect, FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import apiClient, { formatApiError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { devLog } from '@/utils/logger';

/**
 * Reset Password Page
 *
 * Password reset form with token verification and Agile Assets branding
 */

type PageStatus = 'loading' | 'invalid' | 'form' | 'success';

interface UserInfo {
  email: string;
  fullName: string;
  isFirstTimeSetup: boolean;
}

export const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { loginWithResponse } = useAuth();

  const [status, setStatus] = useState<PageStatus>('loading');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Guard against the auto-reload wiping typed new-password input mid-session.
  useUnsavedGuard(password.length > 0 || confirmPassword.length > 0, 'reset-password');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('invalid');
        setError(t('auth_reset_error_invalid_link'));
        return;
      }

      try {
        const response = await apiClient.get(`/api/auth/reset-password/${token}`);

        if (response.data.success && response.data.data) {
          setUserInfo({
            email: response.data.data.email,
            fullName: response.data.data.fullName,
            isFirstTimeSetup: response.data.data.isFirstTimeSetup ?? false,
          });
          setStatus('form');
        } else {
          setStatus('invalid');
          setError(t('auth_reset_error_expired_link'));
        }
      } catch (err) {
        devLog.error('Token verification failed:', err);
        const apiError = formatApiError(err);
        setStatus('invalid');
        setError(apiError.message || t('auth_reset_error_expired_link'));
      }
    };

    verifyToken();
  }, [token, t]);

  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!password) {
      setError(t('auth_reset_error_password_required'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth_reset_error_password_min_length'));
      return;
    }

    if (!confirmPassword) {
      setError(t('auth_reset_error_confirm_required'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth_reset_error_passwords_mismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post(`/api/auth/reset-password/${token}`, {
        password,
      });

      if (response.data.success && response.data.data) {
        // Auto-login with the returned tokens
        const { accessToken, user } = response.data.data;
        await loginWithResponse(accessToken, user);
        // Redirect to dashboard
        navigate('/');
      } else if (response.data.success) {
        // Fallback if no auth data returned (shouldn't happen)
        setStatus('success');
      } else {
        setError(t('auth_reset_error_failed'));
      }
    } catch (err) {
      devLog.error('Password reset failed:', err);
      const apiError = formatApiError(err);
      setError(apiError.message || t('auth_reset_error_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render based on status
  const renderContent = () => {
    if (status === 'loading') {
      return (
        <>
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
              {t('auth_reset_verifying_link')}
            </h1>
            <div className="flex justify-center">
              <svg
                className="animate-spin h-8 w-8 text-secondary-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          </div>
        </>
      );
    }

    if (status === 'invalid') {
      return (
        <>
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
              {t('auth_reset_invalid_heading')}
            </h1>
            <div
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
              role="alert"
            >
              {error}
            </div>
            <p className="text-gray-600 text-sm">
              {t('auth_reset_invalid_message')}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {t('auth_reset_invalid_request_new')}
            </p>
          </div>

          <div className="flex items-center justify-between mt-5 sm:mt-8 mb-3">
            <Link
              to="/login"
              className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
            >
              ← {t('auth_reset_back_to_login')}
            </Link>
            <Link
              to="/forgot-password"
              className="text-[11px] sm:text-[12px] text-bs-secondary hover:text-secondary-600 font-semibold transition-colors"
            >
              {t('auth_reset_request_new_link')}
            </Link>
          </div>
        </>
      );
    }

    if (status === 'success') {
      const isSetup = userInfo?.isFirstTimeSetup;
      return (
        <>
          <div className="text-center mb-4 sm:mb-8">
            <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">
              {t(isSetup ? 'auth_set_success_heading' : 'auth_reset_success_heading')}
            </h1>
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
              {t(isSetup ? 'auth_set_success_message' : 'auth_reset_success_message')}
            </div>
            <p className="text-gray-600">
              {t('auth_reset_success_login_now')}
            </p>
          </div>

          <div className="flex items-center justify-center mt-5 sm:mt-8 mb-3">
            <Link
              to="/login"
              className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
            >
              ← {t('auth_reset_go_to_login')}
            </Link>
          </div>
        </>
      );
    }

    // Form state
    const isSetup = userInfo?.isFirstTimeSetup;
    return (
      <>
        <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 text-center mb-5 sm:mb-8 font-wagner">
          {t(isSetup ? 'auth_set_heading' : 'auth_reset_heading')}
        </h1>

        <p className="text-center text-gray-600 mb-2 text-sm">
          {t(isSetup ? 'auth_set_hello' : 'auth_reset_hello')} <strong>{userInfo?.fullName}</strong>
        </p>
        <p className="text-center text-gray-600 mb-4 sm:mb-8 text-sm">
          {t(isSetup ? 'auth_set_enter_password' : 'auth_reset_enter_new_password')}
        </p>

        {/* Error message */}
        {error && (
          <div
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* Reset password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              inputSize="sm"
              labelVisible
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth_reset_placeholder_password')}
              disabled={isSubmitting}
              autoComplete="new-password"
              required
              minLength={8}
              label={t('auth_reset_label_password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 bottom-0 h-[34px] lg:h-11 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined !text-[18px]">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>

          <div className="relative">
            <Input
              inputSize="sm"
              labelVisible
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth_reset_placeholder_confirm')}
              disabled={isSubmitting}
              autoComplete="new-password"
              required
              label={t('auth_reset_label_confirm')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 bottom-0 h-[34px] lg:h-11 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined !text-[18px]">
                {showConfirmPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>

          <div className="text-center">
            <Button
              type="submit"
              variant="secondary"
              size="lg"
              loading={isSubmitting}
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t(isSetup ? 'auth_set_button' : 'auth_reset_button')}
            </Button>
          </div>

        </form>

        <div className="flex items-center justify-center mt-5 sm:mt-8 mb-3">
          <Link
            to="/login"
            className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
          >
            ← {t('auth_reset_back_to_login')}
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* Reset Password card */}
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.log('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
            }}
            onLoad={(e) => {
              devLog.log('Logo loaded successfully from:', e.currentTarget.src);
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('auth_register_tagline_creating')} <span className="text-bs-secondary font-bold">{t('auth_register_tagline_brand_value')}</span>.
            <br />
            {t('auth_register_tagline_one_asset')}
            <br />
            {t('auth_register_tagline_content_marketing')} <span className="text-bs-secondary font-bold">{t('auth_register_tagline_next_level')}</span>.
          </p>
        </div>

        {/* Dynamic content based on status */}
        {renderContent()}

        {/* Footer — stuck to bottom of card */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Made with <span className="text-bs-secondary">❤️</span> by{' '}
          <a href="https://overw8.de" target="_blank" rel="noopener noreferrer" className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors">OVERW8</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/house-rules/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Terms</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/packages-pricing/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Pricing</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/about/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">About</a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
